import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Card } from "@/shared/components/ui/Card"
import {
  FormField,
  FormSection,
  NumericInput,
  TextAreaField,
  DateTimeField,
  ImageUploadField,
  ParasiteSelect,
} from "@/shared/components/forms"
import { fishHealthSchema, type FishHealthFormData } from "../schemas/fishHealthSchema"

interface FishHealthFormProps {
  defaultValues?: Partial<FishHealthFormData>
  onSubmit: (data: FishHealthFormData) => Promise<void>
  isLoading: boolean
}

/**
 * Form for creating/editing fish health checks.
 */
export function FishHealthForm({ defaultValues, onSubmit, isLoading }: FishHealthFormProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FishHealthFormData>({
    resolver: zodResolver(fishHealthSchema),
    defaultValues: {
      feedType: "",
      averageWeight: undefined,
      sampleSize: undefined,
      checkedAt: new Date(),
      mortalityCount: undefined,
      parasiteCodes: [],
      behaviorObservations: "",
      physicalSymptoms: "",
      treatmentApplied: "",
      veterinarianNotes: "",
      imageIds: [],
      ...defaultValues,
    },
  })

  const veterinarianNotes = watch("veterinarianNotes") || ""

  // Count errors in each section for badges
  const basicErrors = [
    errors.feedType,
    errors.averageWeight,
    errors.sampleSize,
    errors.checkedAt,
  ].filter(Boolean).length
  const healthErrors = [errors.mortalityCount, errors.parasiteCodes].filter(Boolean).length
  const symptomsErrors = [errors.behaviorObservations, errors.physicalSymptoms].filter(Boolean).length
  const treatmentErrors = [errors.treatmentApplied, errors.veterinarianNotes].filter(Boolean).length
  const notesErrors = errors.imageIds ? 1 : 0

  const onFormSubmit = handleSubmit(async (data) => {
    await onSubmit(data)
  })

  return (
    <form id="fish-health-form" onSubmit={onFormSubmit} className="space-y-4">
      {/* Basic Information - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Basic Information" defaultExpanded={true} errorCount={basicErrors}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Feed Type" required error={errors.feedType}>
              <input
                type="text"
                {...register("feedType")}
                placeholder="e.g., Pellet 3mm, Starter Feed, etc."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
                  errors.feedType
                    ? "border-red-300 focus:ring-red-500/20 focus:border-red-500"
                    : "border-slate-200/50 focus:ring-emerald-500/20 focus:border-emerald-500"
                }`}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Check Date & Time" required error={errors.checkedAt}>
              <DateTimeField
                name="checkedAt"
                control={control}
                showTime={true}
                maxDate={new Date()}
              />
            </FormField>

            <FormField label="Average Weight" required error={errors.averageWeight} hint="Per fish sample">
              <NumericInput
                {...register("averageWeight", { valueAsNumber: true })}
                unit="g"
                placeholder="0.0"
                hasError={!!errors.averageWeight}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Sample Size" required error={errors.sampleSize} hint="Number of fish sampled">
              <NumericInput
                {...register("sampleSize", { valueAsNumber: true })}
                placeholder="10"
                hasError={!!errors.sampleSize}
                disabled={isLoading}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Health Assessment - Always expanded */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Health Assessment" defaultExpanded={true} errorCount={healthErrors}>
          <div className="space-y-4">
            <FormField label="Mortality Count" error={errors.mortalityCount} hint="Deaths since last check (optional)">
              <NumericInput
                {...register("mortalityCount", {
                  setValueAs: (v: string) => v === "" ? undefined : Number(v)
                })}
                placeholder="0"
                hasError={!!errors.mortalityCount}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Parasites Observed" error={errors.parasiteCodes} hint="Search and select any parasites found">
              <Controller
                name="parasiteCodes"
                control={control}
                render={({ field }) => (
                  <ParasiteSelect
                    value={field.value || []}
                    onChange={field.onChange}
                    disabled={isLoading}
                    hasError={!!errors.parasiteCodes}
                  />
                )}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Symptoms - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Symptoms & Observations" defaultExpanded={false} errorCount={symptomsErrors}>
          <div className="space-y-4">
            <FormField label="Behavior Observations" error={errors.behaviorObservations} hint="Optional">
              <TextAreaField
                {...register("behaviorObservations")}
                placeholder="e.g., Lethargy, erratic swimming, surface gasping..."
                maxLength={2000}
                rows={3}
                hasError={!!errors.behaviorObservations}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Physical Symptoms" error={errors.physicalSymptoms} hint="Optional">
              <TextAreaField
                {...register("physicalSymptoms")}
                placeholder="e.g., Fin erosion, lesions, discoloration, swelling..."
                maxLength={2000}
                rows={3}
                hasError={!!errors.physicalSymptoms}
                disabled={isLoading}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Treatment - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Treatment & Notes" defaultExpanded={false} errorCount={treatmentErrors}>
          <div className="space-y-4">
            <FormField label="Treatment Applied" error={errors.treatmentApplied} hint="Optional">
              <TextAreaField
                {...register("treatmentApplied")}
                placeholder="e.g., Salt bath 5ppt for 30 min, Oxytetracycline 50mg/kg..."
                maxLength={1000}
                rows={2}
                hasError={!!errors.treatmentApplied}
                disabled={isLoading}
              />
            </FormField>

            <FormField label="Veterinarian Notes" error={errors.veterinarianNotes} hint="Optional professional observations">
              <TextAreaField
                {...register("veterinarianNotes")}
                placeholder="Professional diagnosis, recommendations, follow-up actions..."
                maxLength={2000}
                currentLength={veterinarianNotes.length}
                rows={4}
                hasError={!!errors.veterinarianNotes}
                disabled={isLoading}
              />
            </FormField>
          </div>
        </FormSection>
      </Card>

      {/* Images - Collapsed by default */}
      <Card className="p-0 overflow-hidden">
        <FormSection title="Images" defaultExpanded={false} errorCount={notesErrors}>
          <FormField label="Health Check Images" error={errors.imageIds} hint="Up to 5 images, max 10MB each">
            <Controller
              name="imageIds"
              control={control}
              render={({ field }) => (
                <ImageUploadField
                  value={field.value || []}
                  onChange={field.onChange}
                  maxFiles={5}
                  disabled={isLoading}
                />
              )}
            />
          </FormField>
        </FormSection>
      </Card>
    </form>
  )
}
